import { EFFECT_TYPES as E } from '../../constants/effects';
import { CONDITIONS as C } from './reactionClasses';

/**
 * Famous reactions with hand-written explanations. These override whatever the reaction
 * engine would work out for the same pair. `products` are plain formulas; the equation is
 * balanced automatically. Alloys and mixtures have `mixtureName` instead of products.
 */
export const CURATED_REACTIONS = [
  // ---------- metals + water ----------
  {
    pair: ['K', 'H2O'],
    products: ['KOH', 'H2'],
    productName: 'Potassium hydroxide + hydrogen gas',
    effect: E.EXPLOSION,
    conditions: C.ROOM,
    title: 'POP! Potassium meets Water',
    explanation:
      'Potassium has one lonely outer electron that it REALLY wants to give away. Water happily takes it, and that swap releases lots of heat very fast. The heat sets the hydrogen gas on fire with a lilac flame and a loud pop!',
    funFact: 'Potassium reacts so fast that it skates across the water on a cushion of gas.',
    safetyNote: 'Never try this at home! Real potassium can explode and burn. Scientists handle it with goggles, gloves and tiny amounts.',
  },
  {
    pair: ['Na', 'H2O'],
    products: ['NaOH', 'H2'],
    productName: 'Sodium hydroxide + hydrogen gas',
    effect: E.FIZZ,
    conditions: C.ROOM,
    title: 'Fizz and whizz! Sodium meets Water',
    explanation:
      'Sodium gives its outer electron to water. That makes hydrogen gas bubbles and heat. The sodium melts into a little silver ball and zooms around the water!',
    funFact: 'Sodium is a cousin of potassium. Potassium is lower on the table, so it reacts even more wildly.',
    safetyNote: 'Only trained scientists should handle pure sodium. It can catch fire in water.',
  },
  {
    pair: ['Li', 'H2O'],
    products: ['LiOH', 'H2'],
    productName: 'Lithium hydroxide + hydrogen gas',
    effect: E.FIZZ,
    conditions: C.ROOM,
    title: 'Gentle bubbles! Lithium meets Water',
    explanation:
      'Lithium is the calmest of the alkali metals. It still gives away its electron to water, but slowly, so you just see steady fizzing bubbles of hydrogen.',
    funFact: 'Going down the alkali metal column (Li, Na, K) each one reacts faster than the one above it.',
    safetyNote: 'Only trained scientists should handle pure lithium.',
  },
  {
    pair: ['Cs', 'H2O'],
    products: ['CsOH', 'H2'],
    productName: 'Caesium hydroxide + hydrogen gas',
    effect: E.EXPLOSION,
    conditions: C.ROOM,
    title: 'KABOOM! Caesium meets Water',
    explanation:
      'Caesium gives up its outer electron more easily than almost any other element. The moment it touches water it explodes, releasing hydrogen and a burst of heat.',
    funFact: 'The explosion can be strong enough to shatter the container, even with a tiny piece!',
    safetyNote: 'Never try this! Caesium explodes on contact with water.',
  },
  {
    pair: ['Ca', 'H2O'],
    products: ['Ca(OH)2', 'H2'],
    productName: 'Calcium hydroxide + hydrogen gas',
    effect: E.FIZZ,
    conditions: C.ROOM,
    title: 'Cloudy bubbles! Calcium meets Water',
    explanation:
      'Calcium slowly swaps electrons with water. It makes hydrogen bubbles and turns the water milky white with calcium hydroxide.',
    funFact: 'Calcium hydroxide is also called "slaked lime" and is used to make plaster.',
    safetyNote: 'Only trained scientists should handle pure calcium metal.',
  },

  // ---------- metals + acid ----------
  {
    pair: ['Zn', 'HCl'],
    products: ['ZnCl2', 'H2'],
    productName: 'Zinc chloride + hydrogen gas',
    effect: E.FIZZ,
    conditions: C.ROOM,
    title: 'Bubble trouble! Zinc meets Acid',
    explanation: 'The acid pulls electrons away from the zinc. The zinc dissolves and lots of hydrogen bubbles fizz up.',
    funFact: 'Scientists collect the hydrogen from this reaction and test it with a lit splint. It makes a squeaky pop!',
    safetyNote: 'Acids can burn skin and eyes. Always wear goggles and gloves.',
  },
  {
    pair: ['Mg', 'HCl'],
    products: ['MgCl2', 'H2'],
    productName: 'Magnesium chloride + hydrogen gas',
    effect: E.FIZZ,
    conditions: C.ROOM,
    title: 'Super fizz! Magnesium meets Acid',
    explanation:
      'Magnesium is more reactive than zinc, so the acid eats it up even faster. You get loads of hydrogen bubbles and the tube gets warm.',
    funFact: 'Scientists compare how fast metals fizz in acid to rank how reactive they are.',
    safetyNote: 'Acids can burn skin and eyes. Always wear goggles and gloves.',
  },

  // ---------- metals + non-metals ----------
  {
    pair: ['Na', 'Cl'],
    products: ['NaCl'],
    productName: 'Sodium chloride (table salt)',
    effect: E.FLASH,
    conditions: C.ROOM,
    title: 'Flash! You made table salt!',
    explanation:
      'Sodium wants to lose one electron and chlorine wants to gain one. They are a perfect match! Sodium hands over its electron in a bright flash, and the two stick together as salt.',
    funFact: 'On their own sodium and chlorine are both dangerous, but together they make the salt you sprinkle on chips!',
    safetyNote: 'Chlorine gas is poisonous and sodium is explosive. Only make salt this way in a professional lab.',
  },
  {
    pair: ['Mg', 'O'],
    products: ['MgO'],
    productName: 'Magnesium oxide',
    effect: E.GLOW,
    conditions: C.HEAT,
    title: 'Dazzling light! Magnesium burns',
    explanation:
      'When magnesium burns in oxygen it gives off a super bright white light. The shiny metal turns into a white powder called magnesium oxide.',
    funFact: 'Old-time cameras used burning magnesium as a flash!',
    safetyNote: 'Burning magnesium is so bright it can hurt your eyes. Never look straight at it.',
  },
  {
    pair: ['Fe', 'O'],
    products: ['Fe2O3'],
    productName: 'Iron(III) oxide (rust)',
    effect: E.SLOW_CHANGE,
    conditions: 'Slowly, when iron is damp',
    title: 'Slowly... Iron turns to Rust',
    explanation:
      'Iron and oxygen react very slowly, especially when it is damp. Over days and weeks the grey iron turns into orange-brown rust.',
    funFact: 'Mars looks red because its dusty surface is full of rust!',
  },
  {
    pair: ['Cu', 'O'],
    products: ['CuO'],
    productName: 'Copper(II) oxide',
    effect: E.SLOW_CHANGE,
    conditions: C.HEAT,
    title: 'Colour change! Copper and Oxygen',
    explanation:
      'When copper is heated in air, oxygen joins onto it and the orange metal turns black. Outdoors, copper slowly turns green instead.',
    funFact: 'The Statue of Liberty is made of copper. It used to be shiny orange-brown, and it turned green over time!',
  },
  {
    pair: ['Al', 'O'],
    products: ['Al2O3'],
    productName: 'Aluminium oxide',
    effect: E.SLOW_CHANGE,
    conditions: C.ROOM,
    title: 'Invisible shield! Aluminium and Oxygen',
    explanation:
      'Aluminium reacts with oxygen straight away, but only on the very surface. It makes a super thin see-through layer that protects the metal underneath.',
    funFact: 'That invisible layer is why soda cans do not rust!',
  },
  {
    pair: ['Fe', 'S'],
    products: ['FeS'],
    productName: 'Iron(II) sulfide',
    effect: E.GLOW,
    conditions: C.HEAT,
    title: 'Glowing hot! Iron meets Sulfur',
    explanation:
      'When iron and sulfur are heated together they join up and glow red hot, making a brand new substance called iron sulfide. It is no longer magnetic like iron!',
    funFact: 'A shiny mineral called "fool\'s gold" is made of iron and sulfur.',
  },
  {
    pair: ['Zn', 'S'],
    products: ['ZnS'],
    productName: 'Zinc sulfide',
    effect: E.FLASH,
    conditions: C.SPARK,
    title: 'WHOOSH! Zinc meets Sulfur',
    explanation:
      'Zinc and sulfur powders sit quietly until they are lit. Then they react all at once in a blinding flash and a big puff of yellow-white smoke.',
    funFact: 'Zinc sulfide glows in the dark after light shines on it. It is used in glow-in-the-dark toys!',
    safetyNote: 'This reaction is fierce and smoky. Only chemistry teachers do it, outside or in a fume cupboard.',
  },
  {
    pair: ['Al', 'Br'],
    products: ['AlBr3'],
    productName: 'Aluminium bromide',
    effect: E.FLASH,
    conditions: C.ROOM,
    title: 'Sparks fly! Aluminium meets Bromine',
    explanation:
      'Drop aluminium into liquid bromine and after a moment it bursts into sparks and flames, turning into aluminium bromide.',
    funFact: 'Bromine is one of only two elements that are liquid at room temperature. The other is mercury.',
    safetyNote: 'Bromine is poisonous and burns skin. This is only for professional labs.',
  },
  {
    pair: ['Al', 'I'],
    products: ['AlI3'],
    productName: 'Aluminium iodide',
    effect: E.FLASH,
    conditions: 'Add one drop of water to start it',
    title: 'Purple smoke! Aluminium meets Iodine',
    explanation:
      'Aluminium and iodine powders do nothing until a single drop of water is added. Then they get so hot that clouds of purple iodine smoke billow out!',
    funFact: 'Here water is a trigger that gets the reaction going, even though it is not used up.',
    safetyNote: 'Iodine smoke stings eyes and lungs. Only teachers do this, in a fume cupboard.',
  },
  {
    pair: ['Sb', 'Cl'],
    products: ['SbCl3'],
    productName: 'Antimony trichloride',
    effect: E.FLASH,
    conditions: C.ROOM,
    title: 'Glittering sparks! Antimony meets Chlorine',
    explanation:
      'Sprinkle powdered antimony into chlorine gas and it bursts into a shower of glittering sparks as it becomes antimony chloride.',
    funFact: 'Ancient Egyptians used an antimony mineral called kohl as eye makeup!',
    safetyNote: 'Chlorine is a poisonous gas. This is only for professional labs.',
  },
  {
    pair: ['Li', 'N'],
    products: ['Li3N'],
    productName: 'Lithium nitride',
    effect: E.SLOW_CHANGE,
    conditions: C.ROOM,
    title: 'The only one that can! Lithium meets Nitrogen',
    explanation:
      'Nitrogen is so lazy that almost no metal reacts with it at room temperature. Lithium is the one exception. It slowly turns into dark reddish lithium nitride.',
    funFact: 'Air is mostly nitrogen, so lithium left out in air slowly turns black!',
    safetyNote: 'Only trained scientists should handle pure lithium.',
  },
  {
    pair: ['Pd', 'H'],
    products: ['PdH'],
    productName: 'Palladium hydride',
    effect: E.SLOW_CHANGE,
    conditions: C.ROOM,
    title: 'A metal sponge! Palladium meets Hydrogen',
    explanation:
      'Palladium soaks up hydrogen like a sponge soaks up water. The tiny hydrogen atoms slip in between the palladium atoms.',
    funFact: 'Palladium can soak up about 900 times its own volume of hydrogen gas!',
  },
  {
    pair: ['Hg', 'S'],
    products: ['HgS'],
    productName: 'Mercury sulfide (cinnabar)',
    effect: E.SLOW_CHANGE,
    conditions: C.GRINDING,
    title: 'Trapped! Mercury meets Sulfur',
    explanation:
      'Rub sulfur powder into mercury and they join even without heating, making mercury sulfide. It traps the dangerous mercury in a solid.',
    funFact: 'Sulfur powder is used to clean up spilled mercury. The red pigment vermilion is also mercury sulfide!',
    safetyNote: 'Mercury is poisonous. Never touch it.',
  },
  {
    pair: ['Ag', 'S'],
    products: ['Ag2S'],
    productName: 'Silver sulfide (tarnish)',
    effect: E.SLOW_CHANGE,
    conditions: C.ROOM,
    title: 'Tarnished! Silver meets Sulfur',
    explanation:
      "Silver doesn't react with oxygen, but it slowly reacts with sulfur, forming a black layer of silver sulfide. That is the tarnish on old silver.",
    funFact: 'Silver polish works by removing this black silver sulfide layer.',
  },
  {
    pair: ['Os', 'O'],
    products: ['OsO4'],
    productName: 'Osmium tetroxide',
    effect: E.SLOW_CHANGE,
    conditions: C.SLOW_AIR,
    title: 'Sneaky and dangerous! Osmium meets Oxygen',
    explanation:
      'Powdered osmium slowly reacts with oxygen in the air to make osmium tetroxide, a gas with a sharp smell.',
    funFact: 'The name osmium comes from the Greek word for smell, because of this gas!',
    safetyNote: 'Osmium tetroxide is very poisonous and can damage eyes. Only experts handle it.',
  },
  {
    pair: ['U', 'F'],
    products: ['UF6'],
    productName: 'Uranium hexafluoride',
    effect: E.FLASH,
    conditions: C.NUCLEAR_LAB,
    title: 'A famous gas! Uranium meets Fluorine',
    explanation:
      'Fluorine grabs six electrons from each uranium atom, making uranium hexafluoride. It turns into a gas when warmed a little.',
    funFact: 'This gas is used to sort uranium atoms by weight to make nuclear fuel!',
    safetyNote: 'This one is radioactive! Scientists only handle it in special labs, with thick shielding.',
  },

  // ---------- alloys ----------
  {
    pair: ['Cu', 'Zn'],
    mixtureName: 'Brass (an alloy)',
    effect: E.MELT,
    conditions: C.MELT,
    title: 'Shiny brass! Copper meets Zinc',
    explanation:
      'Copper and zinc do not react, but melted together they blend into brass, a golden-coloured alloy that is harder than copper.',
    funFact: 'Trumpets, trombones and door knobs are often made of brass!',
    safetyNote: 'Melted metal is dangerously hot. Only experts melt metals, in special furnaces.',
  },
  {
    pair: ['Cu', 'Sn'],
    mixtureName: 'Bronze (an alloy)',
    effect: E.MELT,
    conditions: C.MELT,
    title: 'The Bronze Age! Copper meets Tin',
    explanation:
      'Melt copper and tin together and they blend into bronze, which is much harder than either metal on its own.',
    funFact: 'Bronze was so important that a whole period of history is called the Bronze Age!',
    safetyNote: 'Melted metal is dangerously hot. Only experts melt metals, in special furnaces.',
  },
  {
    pair: ['C', 'Fe'],
    mixtureName: 'Steel (iron with a little carbon)',
    effect: E.MELT,
    conditions: C.MELT,
    title: 'Super strong steel! Iron meets Carbon',
    explanation:
      'Adding a tiny bit of carbon to melted iron makes steel. The small carbon atoms get in the way when iron atoms try to slide, making it much stronger.',
    funFact: 'Most steel is less than 2 parts carbon for every 100 parts iron!',
    safetyNote: 'Melted metal is dangerously hot. Only experts melt metals, in special furnaces.',
  },
  {
    pair: ['K', 'Na'],
    mixtureName: 'NaK (a liquid alloy)',
    effect: E.MELT,
    conditions: C.ROOM,
    title: 'A liquid metal! Sodium meets Potassium',
    explanation:
      'Sodium and potassium are both solid, but pressed together they melt into a liquid alloy called NaK, even at room temperature!',
    funFact: 'NaK has been used to carry heat away in some nuclear reactors.',
    safetyNote: 'NaK can catch fire in air and explodes with water. Only experts handle it.',
  },
  {
    pair: ['Al', 'Ga'],
    mixtureName: 'Gallium soaked into aluminium',
    effect: E.MELT,
    conditions: C.ROOM,
    title: 'Crumble! Gallium meets Aluminium',
    explanation:
      'Liquid gallium seeps in between the grains of aluminium and breaks them apart, so a strong aluminium bar becomes weak enough to snap with your fingers.',
    funFact: 'Gallium melts in your hand, but airlines ban it on planes because it can weaken aluminium!',
    safetyNote: 'Only do this with a grown-up, and keep gallium away from anything made of aluminium.',
  },
  {
    pair: ['Au', 'Hg'],
    mixtureName: 'Gold amalgam',
    effect: E.MELT,
    conditions: C.ROOM,
    title: 'Gold disappears! Gold meets Mercury',
    explanation:
      'Gold dissolves into liquid mercury, making a soft silvery paste called an amalgam. Heat it and the mercury boils away, leaving the gold behind.',
    funFact: 'Gold miners once used mercury to collect tiny flecks of gold, which polluted many rivers.',
    safetyNote: 'Mercury is poisonous. Never touch it.',
  },
  {
    pair: ['Al', 'Hg'],
    mixtureName: 'Aluminium amalgam (growing white "fur")',
    effect: E.SLOW_CHANGE,
    conditions: C.ROOM,
    title: 'Hairy metal! Aluminium meets Mercury',
    explanation:
      'Mercury strips away the invisible shield on aluminium. Then air attacks the metal so fast that white, hair-like strands of aluminium oxide grow out of it!',
    funFact: 'This is another reason mercury is banned on aeroplanes.',
    safetyNote: 'Mercury is poisonous. Never touch it.',
  },
  {
    pair: ['Fe', 'Hg'],
    mixtureName: 'Nothing: iron and mercury do not mix',
    effect: E.NONE,
    conditions: C.NONE,
    title: 'Iron says no! Iron meets Mercury',
    explanation:
      'Mercury dissolves many metals, but not iron. The two simply refuse to mix.',
    funFact: 'That is why mercury is shipped in iron flasks!',
    safetyNote: 'Mercury is poisonous. Never touch it.',
  },

  // ---------- non-metals ----------
  {
    pair: ['H', 'O'],
    products: ['H2O'],
    productName: 'Water',
    effect: E.EXPLOSION,
    conditions: C.SPARK,
    title: 'BANG! You made Water!',
    explanation:
      'Hydrogen and oxygen join together to make water. The new bonds release a lot of energy all at once, so it goes BANG!',
    funFact: 'Rockets like the Space Shuttle used hydrogen and oxygen as fuel. The exhaust was mostly steam!',
    safetyNote: 'Hydrogen gas can explode. Keep it far away from flames.',
  },
  {
    pair: ['H', 'F'],
    products: ['HF'],
    productName: 'Hydrogen fluoride',
    effect: E.EXPLOSION,
    conditions: C.ROOM,
    title: 'Instant BANG! Hydrogen meets Fluorine',
    explanation:
      'Fluorine is so reactive that it explodes with hydrogen straight away, even in the dark and even when it is freezing cold.',
    funFact: 'Hydrogen fluoride dissolved in water can eat through glass!',
    safetyNote: 'Fluorine and hydrogen fluoride are extremely dangerous. Only a few expert labs use them.',
  },
  {
    pair: ['H', 'Cl'],
    products: ['HCl'],
    productName: 'Hydrogen chloride',
    effect: E.FLASH,
    conditions: C.LIGHT,
    title: 'Snap! You made Hydrogen Chloride',
    explanation:
      'Hydrogen and chlorine share electrons to make hydrogen chloride. In sunlight the reaction can happen in a sudden flash!',
    funFact: 'Dissolve hydrogen chloride in water and you get hydrochloric acid.',
    safetyNote: 'Both of these gases are dangerous. This is a job for professional chemists only.',
  },
  {
    pair: ['H', 'N'],
    products: ['NH3'],
    productName: 'Ammonia',
    effect: E.SLOW_CHANGE,
    conditions: C.FACTORY,
    title: 'Feeding the world! Nitrogen meets Hydrogen',
    explanation:
      'Nitrogen hardly ever reacts, so factories squeeze it together with hydrogen at huge pressure over a hot iron catalyst. Slowly they join into ammonia.',
    funFact: 'Ammonia made this way is used in fertiliser that grows about half the food in the world!',
    safetyNote: 'Ammonia has a very strong smell and stings eyes and lungs.',
  },
  {
    pair: ['C', 'O'],
    products: ['CO2'],
    productName: 'Carbon dioxide',
    effect: E.GLOW,
    conditions: C.SPARK,
    title: 'Glowing embers! Carbon burns',
    explanation:
      'Carbon joins with oxygen and releases heat and light. That is what happens when charcoal glows on a barbecue. The gas made is carbon dioxide.',
    funFact: 'You breathe out carbon dioxide every time you exhale!',
    safetyNote: 'Fire is hot and dangerous. Always have a grown-up around.',
  },
  {
    pair: ['N', 'O'],
    products: ['NO'],
    productName: 'Nitric oxide',
    effect: E.FLASH,
    conditions: C.LIGHTNING,
    title: 'Made by lightning! Nitrogen meets Oxygen',
    explanation:
      'Air is full of nitrogen and oxygen, but they almost never react. Only the huge heat of a lightning bolt or a car engine joins them into nitric oxide.',
    funFact: 'Lightning makes nitrogen compounds that fall with the rain and help plants grow!',
  },
  {
    pair: ['O', 'S'],
    products: ['SO2'],
    productName: 'Sulfur dioxide',
    effect: E.GLOW,
    conditions: C.SPARK,
    title: 'A blue flame! Sulfur burns',
    explanation: 'Sulfur burns with a pretty blue flame, joining with oxygen to make sulfur dioxide gas.',
    funFact: 'Volcanoes puff out tonnes of sulfur dioxide!',
    safetyNote: 'Sulfur dioxide is a choking gas. Never breathe it in.',
  },
  {
    pair: ['O', 'P'],
    products: ['P4O10'],
    productName: 'Phosphorus pentoxide',
    effect: E.GLOW,
    conditions: C.ROOM,
    title: 'It glows in the dark! Phosphorus burns',
    explanation:
      'White phosphorus reacts with oxygen so easily that it glows in the dark and can catch fire all by itself, making a thick white smoke of phosphorus pentoxide.',
    funFact: 'The name phosphorus means "light bringer" in Greek!',
    safetyNote: 'White phosphorus is poisonous and catches fire by itself. It is kept under water.',
  },
  {
    pair: ['F', 'S'],
    products: ['SF6'],
    productName: 'Sulfur hexafluoride',
    effect: E.FLASH,
    conditions: C.ROOM,
    title: 'A super-heavy gas! Sulfur meets Fluorine',
    explanation:
      'Sulfur burns in fluorine, and each sulfur atom grabs six fluorine atoms. The result is sulfur hexafluoride, a very heavy and very calm gas.',
    funFact: 'If you breathe in sulfur hexafluoride your voice goes super deep, the opposite of helium!',
    safetyNote: 'Fluorine is extremely dangerous. Only a few expert labs ever use it.',
  },
  {
    pair: ['C', 'Si'],
    products: ['SiC'],
    productName: 'Silicon carbide (carborundum)',
    effect: E.GLOW,
    conditions: C.FURNACE,
    title: 'Almost as hard as diamond! Silicon meets Carbon',
    explanation:
      'In a scorching furnace, silicon and carbon lock together into silicon carbide, a crystal almost as hard as diamond.',
    funFact: 'Silicon carbide is the gritty stuff on sandpaper and is used in car brakes!',
  },
  {
    pair: ['F', 'Xe'],
    products: ['XeF2'],
    productName: 'Xenon difluoride',
    effect: E.SLOW_CHANGE,
    conditions: 'In sunlight or when heated',
    title: 'A noble gas reacts! Xenon meets Fluorine',
    explanation:
      'Noble gases almost never react, but fluorine is strong enough to pull electrons from xenon, making white crystals of xenon difluoride.',
    funFact: 'Until 1962, scientists thought noble gases could never make compounds!',
    safetyNote: 'Fluorine is extremely dangerous. Only a few expert labs ever use it.',
  },

  // ---------- with water and acid ----------
  {
    pair: ['F', 'H2O'],
    products: ['HF', 'O2'],
    productName: 'Hydrogen fluoride + oxygen gas',
    effect: E.FLASH,
    conditions: C.ROOM,
    title: 'Fluorine burns water!',
    explanation:
      'Fluorine is so greedy for electrons that it even rips water apart, grabbing the hydrogen and setting the oxygen free.',
    funFact: 'Almost nothing else can make water give up its oxygen like this!',
    safetyNote: 'Fluorine is extremely dangerous. Only a few expert labs ever use it.',
  },
  {
    pair: ['Cl', 'H2O'],
    products: ['HCl', 'HClO'],
    productName: 'Hydrochloric acid + hypochlorous acid (chlorine water)',
    effect: E.SLOW_CHANGE,
    conditions: C.ROOM,
    title: 'Pool water! Chlorine meets Water',
    explanation:
      'Chlorine dissolves in water and partly reacts with it, making two weak acids. One of them kills germs.',
    funFact: 'This is why chlorine keeps swimming pools clean!',
    safetyNote: 'Chlorine gas is poisonous. This is only for professional labs.',
  },
  {
    pair: ['Br', 'H2O'],
    products: ['HBr', 'HBrO'],
    productName: 'Bromine water',
    effect: E.SLOW_CHANGE,
    conditions: C.ROOM,
    title: 'Orange water! Bromine meets Water',
    explanation:
      'A little bromine dissolves in water, turning it orange, and a tiny amount of it reacts to make weak acids.',
    funFact: 'Chemists use orange bromine water as a test: it goes clear when it meets some oily chemicals!',
    safetyNote: 'Bromine is poisonous and burns skin. This is only for professional labs.',
  },
  {
    pair: ['C', 'H2O'],
    products: ['CO', 'H2'],
    productName: 'Carbon monoxide + hydrogen (water gas)',
    effect: E.SLOW_CHANGE,
    conditions: C.STEAM,
    title: 'Water gas! Carbon meets Steam',
    explanation:
      'Blow steam over red-hot carbon and the carbon steals the oxygen from the water, leaving a mixture of two fuel gases called water gas.',
    funFact: 'Before natural gas, many cities lit their street lamps with gas made like this!',
    safetyNote: 'Carbon monoxide is a deadly gas you cannot smell or see.',
  },
  {
    pair: ['H2O', 'P'],
    mixtureName: 'Nothing: phosphorus is kept safe under water',
    effect: E.NONE,
    conditions: C.NONE,
    title: 'Safe under water! Phosphorus meets Water',
    explanation:
      'White phosphorus does not react with water at all. In fact, water protects it from the air, where it would catch fire by itself.',
    funFact: 'Chemists store white phosphorus under water to keep it safe!',
    safetyNote: 'White phosphorus is poisonous. Only experts handle it.',
  },
  {
    pair: ['H2O', 'HCl'],
    mixtureName: 'Dilute hydrochloric acid',
    effect: E.NONE,
    conditions: C.ROOM,
    title: 'Just a bit weaker! Acid meets Water',
    explanation:
      'Pouring hydrochloric acid into water does not make anything new. It just spreads the acid out, making it weaker.',
    funFact: 'Chemists always add acid to water, never water to acid, because it can spit and splash!',
    safetyNote: 'Acids can burn skin and eyes. Always wear goggles and gloves.',
  },
];
